import React, { Component } from "react";
import "./DesignTeamModal.css";
import PlaceholderImage from "../../../img/team-logos/black-background-logo.png";

const TARGET_IMAGE_COUNT = 3;

const padImagesToTarget = (images) => {
  const source = Array.isArray(images) ? images : [];
  const padded = [...source];
  while (padded.length < TARGET_IMAGE_COUNT) {
    padded.push(PlaceholderImage);
  }
  return padded.slice(0, TARGET_IMAGE_COUNT);
};

export class DesignTeamModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeTabKey: "overview",
    };
  }

  componentDidMount() {
    document.addEventListener("keydown", this.handleKeyDown);
    document.body.style.overflow = "hidden";
  }

  componentDidUpdate(prevProps) {
    if (prevProps.team?.id !== this.props.team?.id) {
      this.setState({ activeTabKey: "overview" });
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
    document.body.style.overflow = "auto";
  }

  handleKeyDown = (e) => {
    if (e.key === "Escape")      this.props.onClose();
    if (e.key === "ArrowLeft")   this.props.onPrev();
    if (e.key === "ArrowRight")  this.props.onNext();
  };

  handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      this.props.onClose();
    }
  };

  setActiveTab = (tabKey) => {
    this.setState({ activeTabKey: tabKey });
  };

  renderTabButton = (tabKey, tabData) => {
    const { activeTabKey } = this.state;

    return (
      <button
        key={tabKey}
        type="button"
        className={`design-modal-tab-button ${
          activeTabKey === tabKey ? "design-modal-tab-button-active" : ""
        }`}
        onClick={() => this.setActiveTab(tabKey)}
      >
        <span className="design-modal-tab-label">{tabData.label}</span>
      </button>
    );
  };

  render() {
    const { team, onClose, onPrev, onNext } = this.props;
    const { activeTabKey } = this.state;

    if (!team) return null;

    const { title, subtitle, images, tabs } = team.modal;

    const displayImages = padImagesToTarget(images);

    const tabEntries = Object.entries(tabs);
    const visibleTabEntries = tabEntries.filter(
      ([, tab]) => tab.description || (tab.bullets && tab.bullets.length > 0)
    );

    const resolvedTabKey =
      visibleTabEntries.find(([key]) => key === activeTabKey)
        ? activeTabKey
        : visibleTabEntries[0]?.[0] ?? activeTabKey;

    const activeTab = tabs[resolvedTabKey];
    const hasAnyContent = visibleTabEntries.length > 0;

    const tabKeyOrder = tabEntries.map(([key]) => key);
    const activeImageIndex = Math.max(
      0,
      Math.min(tabKeyOrder.indexOf(resolvedTabKey), displayImages.length - 1)
    );

    return (
      <div className="design-modal-overlay" onClick={this.handleOverlayClick}>
        <div className="design-modal">

          {/* Top-right controls: prev arrow, next arrow, close */}
          <div className="design-modal-controls">
            <button
              className="design-modal-nav-btn"
              onClick={onPrev}
              type="button"
              aria-label="Previous team"
            >
              &#8249;
            </button>
            <button
              className="design-modal-nav-btn"
              onClick={onNext}
              type="button"
              aria-label="Next team"
            >
              &#8250;
            </button>
            <span className="design-modal-controls-divider" aria-hidden="true" />
            <button
              className="design-modal-close"
              onClick={onClose}
              type="button"
              aria-label="Close popup"
            >
              &#215;
            </button>
          </div>

          <div className="design-modal-header">
            <h2 className="design-modal-title">{title}</h2>
            {subtitle && <p className="design-modal-subtitle">{subtitle}</p>}
          </div>

          <div className="design-modal-divider" />

          <div className="design-modal-body">
            <div className="design-modal-left-column">
              <div className="design-modal-image-container">
                <div
                  className="design-modal-image-track"
                  style={{
                    transform: `translateX(-${activeImageIndex * 100}%)`,
                  }}
                >
                  {displayImages.map((image, index) => (
                    <div
                      key={`${image}-${index}`}
                      className="design-modal-image-slide"
                    >
                      <img
                        src={image}
                        alt={`${title} ${index + 1}`}
                        className="design-modal-image"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {displayImages.length > 1 && (
                <div className="design-modal-image-dots">
                  {displayImages.map((_, index) => {
                    const targetTabKey = tabKeyOrder[index];
                    return (
                      <button
                        key={index}
                        type="button"
                        className={`design-modal-image-dot ${
                          activeImageIndex === index ? "design-modal-image-dot-active" : ""
                        }`}
                        onClick={() =>
                          targetTabKey && this.setActiveTab(targetTabKey)
                        }
                        aria-label={`Go to image ${index + 1}`}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            <div className="design-modal-right-column">
              {hasAnyContent ? (
                <>
                  {/* Only render the tab row when more than one tab has content */}
                  {visibleTabEntries.length > 1 && (
                    <div className="design-modal-tab-row">
                      {visibleTabEntries.map(([tabKey, tabData]) =>
                        this.renderTabButton(tabKey, tabData)
                      )}
                    </div>
                  )}

                  <div className="design-modal-content-card">
                    <div className="design-modal-content-scroll">
                      {activeTab.description && (
                        <p className="design-modal-description">
                          {activeTab.description}
                        </p>
                      )}

                      {activeTab.bullets && activeTab.bullets.length > 0 && (
                        <ul className="design-modal-bullets">
                          {activeTab.bullets.map((bullet, index) => (
                            <li key={index}>{bullet}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                /* No content at all — show a subtle placeholder */
                <div className="design-modal-coming-soon">
                  <p>Details coming soon.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default DesignTeamModal;
